import React, {
  useMemo, useCallback, useState, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { Row, Col, DatePicker } from 'antd';
import { Dropdown } from 'antd-v5';
import { CheckOutlined, DownOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import moment from 'moment';

import DateRangePickerButtons from '../common/datepicker/DateRangePickerButtons';
import BorderlessButton from '../common/buttons/BorderlessButton';
import Colors from '../constants/Colors';
import { SCHEDULE_DATE_PICKER_TREE_OPTS } from './schedule.constants';
import { getWeekStart } from './scheduleHelpers';

const formatWeekRange = (date, viewType) => {
  const weekStart = getWeekStart(date);
  const isWeekdays = viewType === 'UserWeekdays' || viewType === 'EquipmentWeekdays';
  const start = isWeekdays ? weekStart.plus({ days: 1 }) : weekStart;
  let endOffset = 6;
  if (viewType === 'UserBiWeekly' || viewType === 'EquipmentBiWeekly') endOffset = 13;
  if (isWeekdays) endOffset = 5;
  const end = weekStart.plus({ days: endOffset });
  const startLabel = start.toFormat('MMM d');
  if (start.month === end.month) return `${startLabel}-${end.day}`;
  return `${startLabel}-${end.toFormat('MMM d')}`;
};

const getFormat = (viewType, date) => {
  switch (viewType) {
    case 'Day':
    case 'UserDay':
    case 'EquipmentDay':
      return 'MMM Do YY';
    case 'UserWeekdays':
    case 'EquipmentWeekdays':
    case 'UserWeek':
    case 'EquipmentWeek':
    case 'Week':
    case 'UserBiWeekly':
    case 'EquipmentBiWeekly':
      return `[${formatWeekRange(date, viewType)}]`;
    case 'Month': return 'MMM YYYY';
    default: return 'MMM YYYY';
  }
};

function ScheduleDatePicker({
  viewType,
  onViewTypeChange,
  onDateChange,
  date,
  onVisibleChanged,
  typeMap,
  style,
}) {
  const company = useSelector((state) => state.settings.company);
  const hasRedesign = company?.settings?.uxRedesignPt1;
  const [pickerOpen, setPickerOpen] = useState(false);

  const pickerValue = useMemo(() => moment(date.toMillis()), [date]);
  const format = useMemo(() => getFormat(viewType, date), [viewType, date]);

  const onButtonPress = useCallback((left) => () => {
    let dateShift;
    switch (viewType) {
      case 'UserDay':
      case 'EquipmentDay':
        dateShift = { day: 1 };
        break;
      case 'UserWeekdays':
      case 'EquipmentWeekdays':
      case 'UserWeek':
      case 'EquipmentWeek':
        dateShift = { week: 1 };
        break;
      case 'UserBiWeekly':
      case 'EquipmentBiWeekly':
        dateShift = { week: 2 };
        break;
      default:
        dateShift = { [viewType.toLowerCase()]: 1 };
        break;
    }
    if (left) {
      onDateChange(date.minus(dateShift));
    } else {
      onDateChange(date.plus(dateShift));
    }
  }, [date, viewType, onDateChange]);

  const getFooter = useCallback(() => {
    let label = '';
    switch (typeMap[viewType]) {
      case typeMap.Week:
        label = 'This Week';
        break;
      case typeMap.Month:
        label = 'This Month';
        break;
      default:
        return null;
    }
    return (
      <BorderlessButton
        title={label}
        onClick={() => {
          onDateChange(DateTime.local());
          setPickerOpen(false);
        }}
        style={{
          color: hasRedesign ? Colors.BLUE_700 : Colors.ONTRACCR_RED,
        }}
      />
    );
  }, [viewType, onDateChange]);

  useEffect(() => {
    if (onVisibleChanged) onVisibleChanged(pickerOpen);
  }, [onVisibleChanged, pickerOpen]);

  const { viewMenuItems, viewLabels } = useMemo(() => {
    const keys = new Set(Object.keys(typeMap));
    const labels = {};
    const items = SCHEDULE_DATE_PICKER_TREE_OPTS
      .map(({
        value,
        title,
        labelPrefix,
        children,
      }) => ({
        key: value,
        label: title,
        popupClassName: 'schedule-view-type-menu',
        children: children
          .filter((child) => keys.has(child.value))
          .map((child) => {
            labels[child.value] = labelPrefix ? `${labelPrefix} ${child.title}` : child.title;
            return {
              key: child.value,
              label: child.title,
              extra: <CheckOutlined className="schedule-view-type-check" />,
            };
          }),
      }))
      .filter((group) => group.children.length > 0);
    return { viewMenuItems: items, viewLabels: labels };
  }, [typeMap]);

  const onViewSelect = useCallback(({ key }) => onViewTypeChange(key), [onViewTypeChange]);

  return (
    <Row gutter={10} style={style}>
      <Col>
        <Dropdown
          trigger={['click']}
          placement="bottomLeft"
          rootClassName="schedule-view-type-menu"
          menu={{
            items: viewMenuItems,
            selectable: true,
            selectedKeys: [viewType],
            onClick: onViewSelect,
          }}
        >
          <button type="button" className="schedule-filter-dropdown-trigger schedule-view-type-trigger">
            <span className="schedule-filter-dropdown-label">{viewLabels[viewType] ?? viewType}</span>
            <DownOutlined />
          </button>
        </Dropdown>
      </Col>
      <Col>
        <Row>
          <Col>
            <DateRangePickerButtons
              left
              onClick={onButtonPress(true)}
              className="schedule-date-range-picker-button-left"
              iconClassName="schedule-date-range-picker-button-icon"
            />
          </Col>
          <Col>
            <DatePicker
              className="schedule-date-picker schedule-date-picker-joined"
              key={viewType}
              picker={typeMap[viewType]}
              onChange={onDateChange}
              value={pickerValue}
              format={format}
              allowClear={false}
              renderExtraFooter={getFooter}
              open={pickerOpen}
              onOpenChange={setPickerOpen}
              style={{ maxWidth: 150 }}
              suffixIcon={null}
            />
          </Col>
          <Col>
            <DateRangePickerButtons
              onClick={onButtonPress(false)}
              className="schedule-date-range-picker-button-right"
              iconClassName="schedule-date-range-picker-button-icon"
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
}

ScheduleDatePicker.propTypes = {
  viewType: PropTypes.string.isRequired,
  onViewTypeChange: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
  date: PropTypes.instanceOf(DateTime).isRequired,
  onVisibleChanged: PropTypes.func,
  typeMap: PropTypes.shape({
    Day: PropTypes.string,
    Week: PropTypes.string,
    Month: PropTypes.string,
    UserDay: PropTypes.string,
    UserBiWeekly: PropTypes.string,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object,
};

ScheduleDatePicker.defaultProps = {
  onVisibleChanged: null,
  typeMap: {},
  style: {},
};

export default ScheduleDatePicker;
